import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import HolographicBg from "@/components/HolographicBg";
import TopBar from "@/components/TopBar";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { navigate("/login"); return; }
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, [user, loading, navigate]);

  if (!user || user.role !== "admin") return null;

  return (
    <div className="relative min-h-screen">
      <HolographicBg />
      <TopBar />
      <main className="relative z-20 px-6 py-8 max-w-7xl mx-auto" data-testid="admin-panel">
        <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-cyan-400/80">— control room —</div>
        <h1 className="font-display text-4xl font-black uppercase mt-2 neon-cyan">Admin Console</h1>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-8">
            <Stat label="Users" v={stats.users} />
            <Stat label="Members" v={stats.members} />
            <Stat label="Events" v={stats.events} />
            <Stat label="Tickets" v={stats.tickets_sold} />
            <Stat label="Pending IG" v={stats.pending_influencers} />
          </div>
        )}

        <Tabs defaultValue="events" className="mt-10">
          <TabsList className="bg-black/50 border border-white/10 flex-wrap h-auto">
            <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
            <TabsTrigger value="attendees" data-testid="tab-attendees">Attendees</TabsTrigger>
            <TabsTrigger value="codes" data-testid="tab-codes">Access Codes</TabsTrigger>
            <TabsTrigger value="influencers" data-testid="tab-influencers">Influencers</TabsTrigger>
            <TabsTrigger value="merch" data-testid="tab-merch">Merch Vault</TabsTrigger>
            <TabsTrigger value="music" data-testid="tab-music">Music</TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-emails">Email Log</TabsTrigger>
          </TabsList>
          <TabsContent value="events"><EventsAdmin /></TabsContent>
          <TabsContent value="attendees"><AttendeesAdmin /></TabsContent>
          <TabsContent value="codes"><CodesAdmin /></TabsContent>
          <TabsContent value="influencers"><InfluencersAdmin /></TabsContent>
          <TabsContent value="merch"><MerchAdmin /></TabsContent>
          <TabsContent value="music"><MusicAdmin /></TabsContent>
          <TabsContent value="emails"><EmailsAdmin /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const Stat = ({ label, v }) => (
  <div className="glass rounded-sm p-4">
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">{label}</div>
    <div className="font-display text-3xl font-black mt-1">{v}</div>
  </div>
);

function EventsAdmin() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: "", date: "", start_at: "", venue: "", price: 10, door_price: 40, capacity: 50, hidden_location: false, description: "", image_url: "" });
  const load = () => api.get("/events").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    // Convert local datetime to ISO with timezone offset — this is the value used by the 24h auto-reveal scheduler
    const startISO = f.start_at ? new Date(f.start_at).toISOString() : "";
    await api.post("/events", { ...f, start_at: startISO, price: parseFloat(f.price), door_price: parseFloat(f.door_price || 0), capacity: parseInt(f.capacity) });
    toast.success("Event created");
    setF({ name: "", date: "", start_at: "", venue: "", price: 10, door_price: 40, capacity: 50, hidden_location: false, description: "", image_url: "" });
    load();
  };
  const del = async (id) => { await api.delete(`/events/${id}`); load(); };
  return (
    <div className="mt-6 space-y-6">
      <form onSubmit={submit} className="glass rounded-sm p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input required placeholder="Event name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="admin-event-name" className="px-3 py-2 bg-black/60 border border-white/15 rounded-sm font-mono text-sm" />
        <input required placeholder="Date & time (display label, e.g. FRI 14 MAR — 23:00)" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="admin-event-date" className="px-3 py-2 bg-black/60 border border-white/15 rounded-sm font-mono text-sm" />
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400/80 mb-1">Real start date & time (for 24h auto-reveal)</label>
          <input required type="datetime-local" value={f.start_at} onChange={(e) => setF({ ...f, start_at: e.target.value })} data-testid="admin-event-start-at" className="w-full px-3 py-2 bg-black/60 border border-cyan-400/20 rounded-sm font-mono text-sm color-scheme-dark" style={{ colorScheme: "dark" }} />
        </div>
        <input required placeholder="Venue (typed here, hidden from public until 24h before if 'hidden' checked)" value={f.venue} onChange={(e) => setF({ ...f, venue: e.target.value })} data-testid="admin-event-venue" className="px-3 py-2 bg-black/60 border border-white/15 rounded-sm font-mono text-sm col-span-full" />
        <div className="col-span-full grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400/80 mb-1">Reserve online</label>
            <input required type="number" step="0.01" min="0" placeholder="10.00" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="admin-event-price" className="w-full px-3 py-2 bg-black/60 border border-cyan-400/20 rounded-sm font-mono text-sm" />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-400/80 mb-1">Cash at door</label>
            <input type="number" step="0.01" min="0" placeholder="40.00 (0 = all upfront)" value={f.door_price} onChange={(e) => setF({ ...f, door_price: e.target.value })} data-testid="admin-event-door-price" className="w-full px-3 py-2 bg-black/60 border border-fuchsia-400/20 rounded-sm font-mono text-sm" />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-1">Capacity</label>
            <input required type="number" placeholder="50" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} data-testid="admin-event-capacity" className="w-full px-3 py-2 bg-black/60 border border-white/15 rounded-sm font-mono text-sm" />
          </div>
        </div>
        <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/70">
          <input type="checkbox" checked={f.hidden_location} onChange={(e) => setF({ ...f, hidden_location: e.target.checked })} /> Hidden location · auto-reveals 24h before
        </label>
        <textarea placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="px-3 py-2 bg-black/60 border border-white/15 rounded-sm font-mono text-sm col-span-full" />
        <button type="submit" data-testid="admin-event-create" className="col-span-full gate py-2 bg-white text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm">Create event</button>
      </form>
      <div className="space-y-2">
        {items.map((e) => (
          <div key={e.id} className="glass rounded-sm p-4 flex items-center justify-between">
            <div>
              <div className="font-display font-bold uppercase">{e.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                {e.date} · {e.venue} · <span className="text-cyan-400">${e.price} reserve</span>
                {e.door_price > 0 && <span className="text-fuchsia-400"> + ${e.door_price} door</span>}
                {" · "}{e.sold}/{e.capacity}
              </div>
            </div>
            <button onClick={() => del(e.id)} className="text-rose-400 font-mono text-xs uppercase tracking-widest hover:underline" data-testid={`admin-delete-event-${e.id}`}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CodesAdmin() {
  const [codes, setCodes] = useState([]);
  const [events, setEvents] = useState([]);
  const [f, setF] = useState({ keyword: "", event_id: "", max_uses: 1 });
  const load = async () => {
    const [c, ev] = await Promise.all([api.get("/access-codes"), api.get("/events")]);
    setCodes(c.data); setEvents(ev.data);
  };
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.event_id) { toast.error("Pick an event"); return; }
    await api.post("/access-codes", { ...f, max_uses: parseInt(f.max_uses) });
    toast.success("Code created");
    load();
  };
  const copyDm = (c) => {
    const ev = events.find((e) => e.id === c.event_id);
    const msg = `Access granted.\n\nEvent: ${ev?.name || ""}\nDate: ${ev?.date || ""}\n\nEnter this code at ${window.location.origin}/access-code to unlock ticket purchase:\n\n${c.code}\n\n— The eye watches over.`;
    navigator.clipboard.writeText(msg);
    toast.success("Message copied — paste into Instagram or text");
  };
  return (
    <div className="mt-6 space-y-6">
      <form onSubmit={submit} className="glass rounded-sm p-5 grid grid-cols-1 sm:grid-cols-4 gap-3">
        <input required placeholder="Keyword (DM: LUNAR)" value={f.keyword} onChange={(e) => setF({ ...f, keyword: e.target.value })} data-testid="admin-code-keyword" className="px-3 py-2 bg-black/60 border border-white/15 rounded-sm font-mono text-sm" />
        <select required value={f.event_id} onChange={(e) => setF({ ...f, event_id: e.target.value })} data-testid="admin-code-event" className="px-3 py-2 bg-black/60 border border-white/15 rounded-sm font-mono text-sm">
          <option value="">Choose event</option>
          {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <input type="number" min={1} value={f.max_uses} onChange={(e) => setF({ ...f, max_uses: e.target.value })} data-testid="admin-code-uses" className="px-3 py-2 bg-black/60 border border-white/15 rounded-sm font-mono text-sm" />
        <button type="submit" data-testid="admin-code-create" className="gate py-2 bg-white text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm">Generate</button>
      </form>
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
        Access codes unlock ticket purchase for the gate. Holders still pay at checkout (Inner Circle members auto-get 15% off). To grant comp / 25% off, use the Influencers tab instead.
      </p>
      <div className="space-y-2">
        {codes.map((c) => (
          <div key={c.id} className="glass rounded-sm p-4 flex items-center justify-between font-mono text-sm gap-3" data-testid={`admin-code-row-${c.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-cyan-400 tracking-[0.3em] text-lg">{c.code}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">Keyword: {c.keyword} · Used {c.uses}/{c.max_uses}</div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => copyDm(c)} data-testid={`admin-code-copy-${c.id}`} className="px-3 py-1 bg-cyan-400 text-black font-display font-bold uppercase tracking-widest text-[10px] rounded-sm">Copy DM</button>
              <button onClick={async () => { await api.delete(`/access-codes/${c.id}`); setCodes(codes.filter((x) => x.id !== c.id)); }} className="px-3 py-1 border border-white/20 text-white font-display font-bold uppercase tracking-widest text-[10px] rounded-sm">Revoke</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InfluencersAdmin() {
  const [apps, setApps] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingTier, setPendingTier] = useState({}); // {appId: 'comp'|'discount'}
  const load = async () => {
    const [a, u] = await Promise.all([api.get("/influencer/applications"), api.get("/admin/users")]);
    setApps(a.data);
    setUsers(u.data.filter((x) => x.role === "influencer" && x.influencer_status === "approved"));
  };
  useEffect(() => { load(); }, []);
  const approve = async (id) => {
    const tier = pendingTier[id] || "comp";
    await api.post(`/influencer/approve/${id}`, { tier });
    toast.success(`Approved as ${tier === "comp" ? "COMP (free)" : "25% off"}. Copy the DM below.`);
    load();
  };
  const reject = async (id) => { await api.post(`/influencer/reject/${id}`); load(); };
  const changeTier = async (uid, tier) => {
    await api.post(`/influencer/tier/${uid}`, { tier });
    toast.success(`Tier updated to ${tier === "comp" ? "COMP" : "25% off"}`);
    load();
  };
  const buildDm = (a) =>
`YOU'VE BEEN GRANTED EXCLUSIVE ACCESS

Welcome to The Secret Soulciety Club VIP tier, ${a.full_name}.

Tier: ${a.tier === "discount" ? "25% off entry" : "Comp entry (free)"}
Login: ${window.location.origin}/influencer/login
Email: ${a.email}
Temp password: ${a.temp_password}

You have 24–48h early access, ${a.tier === "discount" ? "25% off every ticket" : "free entry to events"}, and the Identity Vault is unlocked. Change your password after first login.

— The eye watches over.`;
  const copy = (a) => { navigator.clipboard.writeText(buildDm(a)); toast.success("DM copied — paste into Instagram"); };

  return (
    <div className="mt-6 space-y-6">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
        Pick a tier when approving: COMP (free entry, deducts from public capacity) or 25% OFF (still buys a ticket, just discounted). You can change a tier later from the Active Influencers list below.
      </p>

      <section>
        <h3 className="font-display text-xl font-bold uppercase">Applications</h3>
        <div className="mt-3 space-y-3">
          {apps.length === 0 && <div className="font-mono text-sm text-white/50">No applications yet.</div>}
          {apps.map((a) => (
            <div key={a.id} className="glass rounded-sm p-4" data-testid={`admin-inf-app-${a.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <div className="font-display font-bold uppercase">{a.full_name} · {a.instagram_handle}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    {a.email} · {a.followers} followers ·{" "}
                    <span className={a.status === "approved" ? "text-fuchsia-400" : a.status === "rejected" ? "text-rose-400" : "text-cyan-400"}>{a.status}</span>
                    {a.tier && <span className="ml-2 text-fuchsia-300">· {a.tier === "discount" ? "25% off" : "comp"}</span>}
                  </div>
                  <div className="font-mono text-xs text-white/60 mt-2 max-w-2xl">{a.pitch}</div>
                </div>
                {a.status === "pending" && (
                  <div className="flex flex-col sm:items-end gap-2">
                    <div className="flex gap-1 text-[10px] font-mono uppercase tracking-widest">
                      {["comp", "discount"].map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setPendingTier({ ...pendingTier, [a.id]: opt })}
                          data-testid={`admin-tier-${opt}-${a.id}`}
                          className={`px-2 py-1 rounded-sm border ${(pendingTier[a.id] || "comp") === opt ? "bg-fuchsia-500 text-black border-fuchsia-500" : "border-white/20 text-white/70 hover:bg-white/10"}`}
                        >
                          {opt === "comp" ? "Comp (free)" : "25% off"}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => approve(a.id)} data-testid={`admin-approve-${a.id}`} className="px-4 py-2 bg-fuchsia-500 text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm">Approve</button>
                      <button onClick={() => reject(a.id)} className="px-4 py-2 border border-white/20 text-white font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm">Reject</button>
                    </div>
                  </div>
                )}
              </div>

              {a.status === "approved" && a.temp_password && (
                <div className="mt-4 border border-fuchsia-500/30 rounded-sm p-4 bg-fuchsia-500/5" data-testid={`admin-inf-credentials-${a.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-400">— credentials to send —</div>
                      <div className="font-mono text-sm text-white/90 mt-2">Email: <span className="text-cyan-400">{a.email}</span></div>
                      <div className="font-mono text-sm text-white/90">Temp password: <span className="text-cyan-400 tracking-widest">{a.temp_password}</span></div>
                      <div className="font-mono text-sm text-white/90">Tier: <span className="text-fuchsia-400">{a.tier === "discount" ? "25% off" : "comp (free)"}</span></div>
                    </div>
                    <button onClick={() => copy(a)} data-testid={`admin-copy-dm-${a.id}`} className="gate px-4 py-2 bg-fuchsia-500 text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm">Copy DM</button>
                  </div>
                  <pre className="mt-3 font-mono text-[11px] text-white/60 whitespace-pre-wrap leading-relaxed border-t border-white/10 pt-3">{buildDm(a)}</pre>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-display text-xl font-bold uppercase">Active influencers ({users.length})</h3>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mt-2">Switch a tier here anytime.</p>
        <div className="mt-3 space-y-2">
          {users.length === 0 && <div className="font-mono text-sm text-white/50">No active influencers.</div>}
          {users.map((u) => (
            <div key={u.id} className="glass rounded-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`admin-inf-active-${u.id}`}>
              <div>
                <div className="font-display font-bold uppercase">{u.full_name}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                  {u.email} · {u.instagram_handle || "—"} ·{" "}
                  <span className="text-fuchsia-400">{u.influencer_tier === "discount" ? "25% off" : "comp"}</span>
                </div>
              </div>
              <div className="flex gap-1 text-[10px] font-mono uppercase tracking-widest">
                {["comp", "discount"].map((opt) => (
                  <button
                    key={opt}
                    onClick={() => changeTier(u.id, opt)}
                    data-testid={`admin-change-tier-${opt}-${u.id}`}
                    className={`px-2 py-1 rounded-sm border ${u.influencer_tier === opt || (!u.influencer_tier && opt === "comp") ? "bg-fuchsia-500 text-black border-fuchsia-500" : "border-white/20 text-white/70 hover:bg-white/10"}`}
                  >
                    {opt === "comp" ? "Comp" : "25% off"}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function MerchAdmin() {
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [f, setF] = useState({ name: "", description: "", image_url: "", tier: "all", stock: 10, price: 0, active: true });
  const [imgUploading, setImgUploading] = useState(false);
  const load = () => api.get("/merch").then(({ data }) => setItems(data)).catch(() => setItems([]));
  const loadOrders = () => api.get("/admin/merch/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  useEffect(() => { load(); loadOrders(); }, []);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/merch/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Prepend REACT_APP_BACKEND_URL so the img renders from anywhere
      const abs = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setF((prev) => ({ ...prev, image_url: abs }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setImgUploading(false);
      e.target.value = ""; // allow re-selecting same file
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.price || Number(f.price) <= 0) { toast.error("Enter a price greater than $0"); return; }
    await api.post("/merch", { ...f, stock: parseInt(f.stock), price: Number(f.price) });
    toast.success("Drop created");
    setF({ name: "", description: "", image_url: "", tier: "all", stock: 10, price: 0, active: true });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this drop from the vault? This cannot be undone.")) return;
    await api.delete(`/merch/${id}`);
    toast.success("Removed");
    load();
  };
  const markShipped = async (order_id) => {
    const tracking = window.prompt("Tracking number (optional):");
    if (tracking === null) return;
    const carrier = window.prompt("Carrier (optional, e.g. USPS/UPS/FedEx):") || "";
    await api.post(`/admin/merch/orders/${order_id}/ship`, { tracking, carrier });
    toast.success("Marked as shipped");
    loadOrders();
  };
  return (
    <div className="mt-6 space-y-8">
      <form onSubmit={submit} className="glass rounded-sm p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input required placeholder="Item name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="admin-merch-name" className="px-3 py-2 bg-black/60 border border-white/15 rounded-sm font-mono text-sm" />
        <input required type="number" step="0.01" min="0" placeholder="Price (USD)" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="admin-merch-price" className="px-3 py-2 bg-black/60 border border-white/15 rounded-sm font-mono text-sm" />

        {/* Product image — mobile camera roll on tap */}
        <div className="col-span-full">
          <label className="block font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-400/80 mb-2">Product image</label>
          <div className="flex items-start gap-3">
            {f.image_url && (
              <img src={f.image_url} alt="preview" className="w-20 h-20 object-cover rounded-sm border border-white/15" data-testid="admin-merch-image-preview" />
            )}
            <div className="flex-1">
              <label className="inline-flex items-center gap-3 cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={uploadImage}
                  disabled={imgUploading}
                  data-testid="admin-merch-image-upload"
                  className="hidden"
                />
                <span className={`px-4 py-2 border border-fuchsia-400/40 text-fuchsia-300 font-display font-bold uppercase tracking-[0.3em] text-[11px] rounded-sm hover:bg-fuchsia-400/10 transition ${imgUploading ? "opacity-50 cursor-wait" : ""}`}>
                  {imgUploading ? "Uploading..." : f.image_url ? "Replace Image" : "Upload From Device"}
                </span>
              </label>
              <p className="font-mono text-[10px] text-white/40 mt-2 leading-relaxed">
                On mobile: opens camera roll or camera. JPG · PNG · WEBP · HEIC · max 12 MB.
              </p>
              {f.image_url && (
                <button
                  type="button"
                  onClick={() => setF({ ...f, image_url: "" })}
                  data-testid="admin-merch-image-clear"
                  className="mt-2 font-mono text-[10px] uppercase tracking-widest text-rose-400/70 hover:text-rose-400"
                >
                  Clear image
                </button>
              )}
            </div>
          </div>
        </div>

        <input type="number" placeholder="Stock" value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value })} data-testid="admin-merch-stock" className="px-3 py-2 bg-black/60 border border-white/15 rounded-sm font-mono text-sm" />
        <select value={f.tier} onChange={(e) => setF({ ...f, tier: e.target.value })} data-testid="admin-merch-tier" className="px-3 py-2 bg-black/60 border border-white/15 rounded-sm font-mono text-sm">
          <option value="all">All influencers</option>
          <option value="premium">Premium tier only</option>
        </select>
        <label className="flex items-center gap-2 font-mono text-xs text-white/70 px-3 col-span-full">
          <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="admin-merch-active" />
          Active (visible in vault)
        </label>
        <textarea required placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="px-3 py-2 bg-black/60 border border-white/15 rounded-sm font-mono text-sm col-span-full" />
        <button type="submit" data-testid="admin-merch-create" className="col-span-full gate py-2 bg-fuchsia-500 text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm">Create drop</button>
      </form>

      <div>
        <h3 className="font-display text-lg font-black uppercase mb-3">Active drops</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.length === 0 && <div className="font-mono text-xs text-white/50">No drops yet.</div>}
          {items.map((m) => (
            <div key={m.id} className="glass rounded-sm p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display font-bold uppercase">{m.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-fuchsia-400 mt-1">{m.tier} · {m.stock} in stock</div>
                </div>
                <div className="font-mono text-sm text-cyan-400">${Number(m.price || 0).toFixed(2)}</div>
              </div>
              <button onClick={() => remove(m.id)} className="mt-3 font-mono text-[10px] uppercase tracking-widest text-rose-400/70 hover:text-rose-400" data-testid={`admin-merch-remove-${m.id}`}>Remove</button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-display text-lg font-black uppercase mb-3">Ship Queue · {orders.filter(o=>o.fulfillment_status==="pending").length} pending</h3>
        <div className="space-y-2">
          {orders.length === 0 && <div className="font-mono text-xs text-white/50">No orders yet.</div>}
          {orders.map((o) => (
            <div key={o.id} className="glass rounded-sm p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-start" data-testid={`admin-merch-order-${o.id}`}>
              <div>
                <div className="font-display font-bold uppercase text-sm">{o.merch_name}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-400/80 mt-1">${Number(o.amount).toFixed(2)}</div>
                <div className="font-mono text-[10px] text-white/40 mt-1">{o.id.slice(0,8)}…</div>
              </div>
              <div className="font-mono text-xs text-white/70">
                <div>{o.buyer_name}</div>
                <div className="text-cyan-300/70">{o.buyer_email}</div>
              </div>
              <div className="font-mono text-[11px] text-white/70 leading-relaxed">
                {o.shipping?.name}<br/>
                {o.shipping?.address1}{o.shipping?.address2 && `, ${o.shipping.address2}`}<br/>
                {o.shipping?.city}, {o.shipping?.region} {o.shipping?.postal}<br/>
                {o.shipping?.country}
                {o.shipping?.phone && <div className="text-white/40">☎ {o.shipping.phone}</div>}
              </div>
              <div className="text-right">
                {o.fulfillment_status === "shipped" ? (
                  <>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">Shipped</div>
                    {o.tracking && <div className="font-mono text-[10px] text-white/50 mt-1">{o.carrier} · {o.tracking}</div>}
                  </>
                ) : (
                  <button onClick={() => markShipped(o.id)} data-testid={`admin-mark-shipped-${o.id}`} className="px-3 py-1.5 border border-fuchsia-400/40 text-fuchsia-300 font-display font-bold uppercase tracking-[0.3em] text-[10px] rounded-sm hover:bg-fuchsia-400/10">
                    Mark shipped
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MusicAdmin() {
  const [tracks, setTracks] = useState([]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/music").then(({ data }) => setTracks(data));
  useEffect(() => { load(); }, []);
  const upload = async (e) => {
    e.preventDefault();
    if (!file || !title) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    try {
      await api.post("/music/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Track uploaded");
      setTitle(""); setFile(null);
      // reset file input by re-rendering
      const fi = document.querySelector('[data-testid="admin-music-file"]');
      if (fi) fi.value = "";
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const addToPlaylist = async (id) => { await api.post(`/music/activate/${id}`); load(); };
  const removeFromPlaylist = async (id) => { await api.post(`/music/deactivate/${id}`); load(); };
  const del = async (id) => { await api.delete(`/music/${id}`); load(); };
  const move = async (id, dir) => {
    const active = tracks.filter((t) => t.active).sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const idx = active.findIndex((t) => t.id === id);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= active.length) return;
    const reordered = [...active];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    await api.post("/music/reorder", { ids: reordered.map((t) => t.id) });
    load();
  };

  const inPlaylist = tracks.filter((t) => t.active).sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const available = tracks.filter((t) => !t.active);

  return (
    <div className="mt-6 space-y-8">
      <form onSubmit={upload} className="glass rounded-sm p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input required placeholder="Track title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="admin-music-title" className="px-3 py-2 bg-black/60 border border-white/15 rounded-sm font-mono text-sm" />
        <input required type="file" accept="audio/*" onChange={(e) => setFile(e.target.files[0])} data-testid="admin-music-file" className="px-3 py-2 bg-black/60 border border-white/15 rounded-sm font-mono text-xs text-white/70" />
        <button type="submit" disabled={uploading} data-testid="admin-music-upload" className="gate py-2 bg-white text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm">{uploading ? "Uploading..." : "Upload"}</button>
      </form>

      <section>
        <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-cyan-400/80">— now playing —</div>
        <h3 className="font-display text-xl font-bold uppercase mt-1">Playlist ({inPlaylist.length})</h3>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mt-2">Tracks play in this order, then loop. Use ↑/↓ to reorder.</p>
        <div className="space-y-2 mt-4">
          {inPlaylist.length === 0 && <div className="font-mono text-sm text-white/50">No tracks in the playlist yet. Add one from "Available tracks" below.</div>}
          {inPlaylist.map((t, i) => (
            <div key={t.id} className="glass rounded-sm p-4 flex items-center justify-between gap-3" data-testid={`admin-playlist-row-${t.id}`}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <span className="font-display text-2xl font-black text-cyan-400 w-8 text-center">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold uppercase truncate">{t.title}</div>
                  <audio controls src={`${BACKEND_URL}${t.url}`} className="mt-2 h-8 w-full max-w-xs" />
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => move(t.id, -1)} disabled={i === 0} data-testid={`admin-music-up-${t.id}`} className="w-8 h-8 flex items-center justify-center border border-white/20 text-white font-mono text-sm rounded-sm disabled:opacity-30 hover:bg-white/10">↑</button>
                <button onClick={() => move(t.id, +1)} disabled={i === inPlaylist.length - 1} data-testid={`admin-music-down-${t.id}`} className="w-8 h-8 flex items-center justify-center border border-white/20 text-white font-mono text-sm rounded-sm disabled:opacity-30 hover:bg-white/10">↓</button>
                <button onClick={() => removeFromPlaylist(t.id)} data-testid={`admin-music-remove-${t.id}`} className="px-3 py-1 border border-white/20 text-white font-display font-bold uppercase tracking-widest text-[10px] rounded-sm hover:bg-white/10">Remove</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-white/50">— library —</div>
        <h3 className="font-display text-xl font-bold uppercase mt-1">Available tracks ({available.length})</h3>
        <div className="space-y-2 mt-4">
          {available.length === 0 && <div className="font-mono text-sm text-white/50">No standby tracks. Upload more above.</div>}
          {available.map((t) => (
            <div key={t.id} className="glass rounded-sm p-4 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold uppercase truncate">{t.title}</div>
                <audio controls src={`${BACKEND_URL}${t.url}`} className="mt-2 h-8 w-full max-w-xs" />
              </div>
              <div className="flex gap-2">
                <button onClick={() => addToPlaylist(t.id)} data-testid={`admin-music-add-${t.id}`} className="px-3 py-1 bg-cyan-400 text-black font-display font-bold uppercase tracking-widest text-[10px] rounded-sm">+ Playlist</button>
                <button onClick={() => del(t.id)} className="px-3 py-1 border border-white/20 text-white font-display font-bold uppercase tracking-widest text-[10px] rounded-sm">Delete</button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function EmailsAdmin() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const load = () => {
    setLoading(true);
    api.get("/admin/emails")
      .then(({ data }) => setEmails(data))
      .catch(() => setEmails([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const stats = {
    total: emails.length,
    delivered: emails.filter((m) => m.delivery?.ok).length,
    failed: emails.filter((m) => m.delivery && m.delivery.ok === false).length,
    unknown: emails.filter((m) => !m.delivery).length,
  };

  const filtered = filter
    ? emails.filter((m) =>
        (m.to || "").toLowerCase().includes(filter.toLowerCase()) ||
        (m.subject || "").toLowerCase().includes(filter.toLowerCase()))
    : emails;

  const fmtWhen = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="mt-6 space-y-4" data-testid="admin-email-log">
      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBadge label="Total" value={stats.total} accent="text-white" />
        <StatBadge label="Delivered" value={stats.delivered} accent="text-emerald-400" />
        <StatBadge label="Failed" value={stats.failed} accent="text-rose-400" />
        <StatBadge label="Unknown" value={stats.unknown} accent="text-white/40" />
      </div>

      {/* Filter + refresh */}
      <div className="flex flex-wrap gap-3 items-center">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by email or subject..."
          data-testid="admin-email-filter"
          className="flex-1 min-w-[220px] px-3 py-2 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-cyan-400/60"
        />
        <button
          onClick={load}
          data-testid="admin-email-refresh"
          className="px-4 py-2 border border-cyan-400/40 text-cyan-300 font-display font-bold uppercase tracking-[0.3em] text-[11px] rounded-sm hover:bg-cyan-400/10"
        >
          Refresh
        </button>
      </div>

      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400/70">
        ◆ Live email delivery log · powered by Resend · newest first
      </p>

      {loading ? (
        <div className="font-mono text-xs text-white/50">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="glass rounded-sm p-6 font-mono text-sm text-white/50 text-center">
          {emails.length === 0 ? "No emails sent yet." : "No matches for that filter."}
        </div>
      ) : (
        filtered.map((m) => {
          const d = m.delivery || {};
          const status = d.ok === true ? "delivered" : d.ok === false ? "failed" : "unknown";
          const statusClass =
            status === "delivered" ? "bg-emerald-400/10 border-emerald-400/40 text-emerald-400"
            : status === "failed"   ? "bg-rose-400/10 border-rose-400/40 text-rose-400"
            : "bg-white/5 border-white/20 text-white/40";
          return (
            <div key={m.id} className="glass rounded-sm p-4" data-testid={`email-log-row-${m.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold uppercase text-sm truncate">{m.subject}</div>
                  <div className="font-mono text-[11px] text-cyan-300/80 mt-1 truncate">To: {m.to || "—"}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-1">{fmtWhen(m.created_at)}</div>
                </div>
                <div className={`px-3 py-1 rounded-sm border font-mono text-[10px] uppercase tracking-[0.3em] ${statusClass}`}>
                  {status}
                </div>
              </div>
              {d.id && (
                <div className="mt-2 font-mono text-[10px] text-white/40">
                  Resend ID: <span className="text-cyan-400/80">{d.id}</span>
                </div>
              )}
              {status === "failed" && (
                <div className="mt-2 font-mono text-xs text-rose-400">
                  Reason: {d.reason || d.error || "unknown"}
                </div>
              )}
              {m.body && (
                <pre className="font-mono text-xs text-white/60 mt-3 whitespace-pre-wrap break-words">{m.body}</pre>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

const StatBadge = ({ label, value, accent }) => (
  <div className="glass rounded-sm p-3">
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">{label}</div>
    <div className={`font-display text-2xl font-black mt-1 ${accent}`}>{value}</div>
  </div>
);

function AttendeesAdmin() {
  const [events, setEvents] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [attendees, setAttendees] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [notifying, setNotifying] = useState(false);

  const load = async () => {
    const { data } = await api.get("/events");
    setEvents(data);
  };

  useEffect(() => { load(); }, []);

  const openEvent = async (id) => {
    setSelectedId(id);
    setLoading(true);
    try {
      const [attRes, wlRes] = await Promise.all([
        api.get(`/admin/events/${id}/attendees`),
        api.get(`/admin/events/${id}/waitlist`).catch(() => ({ data: { waitlist: [] } })),
      ]);
      setAttendees(attRes.data.attendees);
      setEvent(attRes.data.event);
      setLocation(attRes.data.event.venue || "");
      setWaitlist(wlRes.data.waitlist || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load attendees");
    } finally {
      setLoading(false);
    }
  };

  const notifyWaitlist = async () => {
    const pending = waitlist.filter((w) => !w.notified_at).length;
    if (pending === 0) { toast.info("Nobody left to notify."); return; }
    const n = window.prompt(`Notify how many? (${pending} pending)`, String(Math.min(pending, 10)));
    if (n === null) return;
    const limit = parseInt(n, 10);
    if (!limit || limit < 1) return;
    setNotifying(true);
    try {
      const { data } = await api.post(`/admin/events/${selectedId}/waitlist/notify`, { limit });
      toast.success(`${data.sent} notified · ${data.failed.length} failed`);
      openEvent(selectedId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Notify failed");
    } finally {
      setNotifying(false);
    }
  };

  const reveal = async () => {
    if (!location.trim() || location.trim().length < 3) {
      toast.error("Enter the exact location before revealing.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/admin/events/${selectedId}/reveal-location`, {
        location: location.trim(),
        save_to_event: true,
      });
      toast.success(`Location revealed — ${data.sent}/${data.attendee_count} email(s) sent`);
      setConfirmOpen(false);
      if (data.failed?.length) {
        toast.warning(`${data.failed.length} failed to deliver`);
      }
      openEvent(selectedId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reveal failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-6" data-testid="admin-attendees-panel">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-400/70">
        Reveal the destination to every soul on the guest list.
      </p>

      {/* Event picker */}
      <div className="mt-4 flex flex-wrap gap-2">
        {events.length === 0 && (
          <div className="font-mono text-xs text-white/50">No events yet. Create one in the Events tab first.</div>
        )}
        {events.map((e) => (
          <button
            key={e.id}
            onClick={() => openEvent(e.id)}
            data-testid={`admin-attendee-event-${e.id}`}
            className={`px-4 py-2 border rounded-sm font-mono text-[11px] uppercase tracking-widest transition ${
              selectedId === e.id
                ? "border-cyan-400 text-cyan-300 bg-cyan-400/10"
                : "border-white/15 text-white/70 hover:border-cyan-400/40 hover:text-cyan-400"
            }`}
          >
            {e.name}
          </button>
        ))}
      </div>

      {selectedId && (
        <>
          {/* Location reveal panel */}
          <div className="mt-8 glass-strong rounded-sm p-6 border border-fuchsia-400/20" data-testid="admin-reveal-panel">
            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-fuchsia-400/80">— the destination —</div>
            <h3 className="font-display text-xl font-black uppercase mt-2">Reveal exact location</h3>
            <p className="font-mono text-[11px] text-white/50 mt-2 leading-relaxed">
              This will send the poetic reveal email — <em>&quot;follow the path in the dark of starlight...&quot;</em> — with the address below to every paid attendee.
            </p>
            <textarea
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              rows={2}
              placeholder="e.g. 42 Wooster St, Basement Level — buzz #333 · Brooklyn NY"
              data-testid="admin-reveal-location-input"
              className="mt-4 w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-fuchsia-400/60 resize-none"
            />
            <div className="mt-4 flex flex-wrap gap-3 items-center">
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={!location.trim() || attendees.length === 0}
                data-testid="admin-reveal-btn"
                className="gate px-6 py-3 bg-fuchsia-500 text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm hover:scale-[1.02] transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Reveal to {attendees.length} attendee{attendees.length === 1 ? "" : "s"}
              </button>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                {attendees.length === 0 ? "No paid attendees yet" : "Emails send instantly"}
              </div>
            </div>
          </div>

          {/* Confirmation modal */}
          {confirmOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
              onClick={() => !sending && setConfirmOpen(false)}
              data-testid="admin-reveal-confirm-overlay"
            >
              <div
                className="glass-strong rounded-sm p-8 max-w-md w-full border border-fuchsia-400/30"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-fuchsia-400/80">— confirm the reveal —</div>
                <h3 className="font-display text-2xl font-black uppercase mt-3">Send to {attendees.length} soul{attendees.length === 1 ? "" : "s"}?</h3>
                <p className="font-mono text-xs text-white/60 mt-4 leading-relaxed">
                  Every paid attendee will receive an email with the destination:
                </p>
                <div className="mt-3 p-3 border border-fuchsia-400/20 bg-fuchsia-400/5 rounded-sm font-mono text-sm text-white/90 break-words">
                  {location.trim()}
                </div>
                <p className="font-mono text-[11px] text-white/40 mt-4">This cannot be undone.</p>
                <div className="mt-6 flex gap-3 justify-end">
                  <button
                    onClick={() => setConfirmOpen(false)}
                    disabled={sending}
                    data-testid="admin-reveal-cancel"
                    className="px-5 py-2 border border-white/20 text-white font-display font-bold uppercase tracking-[0.3em] text-[11px] rounded-sm hover:bg-white/5 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={reveal}
                    disabled={sending}
                    data-testid="admin-reveal-confirm"
                    className="px-5 py-2 bg-fuchsia-500 text-black font-display font-bold uppercase tracking-[0.3em] text-[11px] rounded-sm hover:scale-[1.02] transition disabled:opacity-50"
                  >
                    {sending ? "Sending..." : "Send Now"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Attendee list */}
          <div className="mt-8">
            <div className="flex items-baseline justify-between">
              <h3 className="font-display text-xl font-black uppercase">Attendees · {attendees.length}</h3>
              {event && (
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{event.name} · {event.date}</div>
              )}
            </div>
            {loading ? (
              <div className="mt-4 font-mono text-sm text-white/50">Loading...</div>
            ) : attendees.length === 0 ? (
              <div className="mt-4 font-mono text-sm text-white/50">No paid attendees for this event yet.</div>
            ) : (
              <div className="mt-4 space-y-2" data-testid="admin-attendees-list">
                {attendees.map((a) => (
                  <div key={a.ticket_id} className="glass rounded-sm p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-attendee-${a.ticket_id}`}>
                    <div>
                      <div className="font-display font-bold uppercase text-sm">{a.name || "—"}</div>
                      <div className="font-mono text-[11px] text-cyan-300/80 mt-0.5">{a.email}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-400/70">{a.source}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-1">QR: {(a.qr_token || "").slice(0, 8)}…</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Waitlist */}
          <div className="mt-10" data-testid="admin-waitlist-section">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h3 className="font-display text-xl font-black uppercase">
                Waitlist · {waitlist.length}
                {waitlist.filter((w) => !w.notified_at).length > 0 && (
                  <span className="ml-3 font-mono text-[11px] text-fuchsia-400/80">
                    ({waitlist.filter((w) => !w.notified_at).length} pending)
                  </span>
                )}
              </h3>
              {waitlist.filter((w) => !w.notified_at).length > 0 && (
                <button
                  onClick={notifyWaitlist}
                  disabled={notifying}
                  data-testid="admin-waitlist-notify-btn"
                  className="px-4 py-2 border border-fuchsia-400/50 text-fuchsia-300 font-display font-bold uppercase tracking-[0.3em] text-[11px] rounded-sm hover:bg-fuchsia-400/10 disabled:opacity-50"
                >
                  {notifying ? "Sending..." : "Notify Waitlist"}
                </button>
              )}
            </div>
            {waitlist.length === 0 ? (
              <div className="mt-4 font-mono text-sm text-white/50">No one on the waitlist yet.</div>
            ) : (
              <div className="mt-4 space-y-2" data-testid="admin-waitlist-list">
                {waitlist.map((w, i) => (
                  <div key={w.id} className="glass rounded-sm p-3 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-waitlist-row-${w.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="font-mono text-[11px] text-white/40 w-8 text-center">#{i + 1}</div>
                      <div>
                        <div className="font-display font-bold uppercase text-sm">{w.full_name}</div>
                        <div className="font-mono text-[11px] text-cyan-300/80">{w.email}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      {w.notified_at ? (
                        <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">Notified</div>
                      ) : (
                        <div className="font-mono text-[10px] uppercase tracking-widest text-fuchsia-400/80">Waiting</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

